import { useState, useEffect } from 'react';

const YOUTUBE_IFRAME_API_URL = 'https://www.youtube.com/iframe_api';
const SCRIPT_ID = 'youtube-iframe-api';

/**
 * useYouTubeIframeAPI — idempotent YouTube IFrame API script loader.
 *
 * - Checks window.YT before appending the script tag (idempotent across mounts).
 * - Returns { ready: boolean } that flips to true once window.onYouTubeIframeAPIReady fires.
 * - Handles script load failure by catching the onerror event.
 * - Multiple component instances share the same script load; the second+ callers
 *   see the ready state immediately if window.YT is already fully initialised.
 *
 * AC 1: Player ready within 3 s on good connection (script load ≤ 3 s on reasonable network).
 * AC 5: Failure surfaces via { ready: false, error: true } so the caller can show an error state.
 * AC 6: Does not trigger autoplay — readiness alone does not start playback.
 */
export const useYouTubeIframeAPI = (): { ready: boolean; error: boolean } => {
  // If window.YT is already fully initialised (e.g. script loaded by a previous mount),
  // start in ready state immediately.
  const [ready, setReady] = useState(() => {
    if (typeof window === 'undefined') return false;
    return !!window.YT && typeof window.YT.Player === 'function';
  });
  const [error, setError] = useState(false);

  useEffect(() => {
    // Already ready — nothing to do.
    if (ready) return;

    // Script already appended by a previous component instance but not yet initialised.
    const existingScript = document.getElementById(SCRIPT_ID) as HTMLScriptElement | null;
    if (existingScript) {
      if (existingScript.dataset.error === 'true') {
        // Previous load failed — remove the dead script tag and retry on a clean slate.
        existingScript.remove();
        // Fall through to re-inject a fresh script below.
      } else {
        // Script is in-flight or succeeded — piggyback on the existing ready callback.
        const existing = window.onYouTubeIframeAPIReady;
        window.onYouTubeIframeAPIReady = () => {
          existing?.();
          setReady(true);
        };
        return;
      }
    }

    // First load (or retry after failure) — inject the script tag.
    const script = document.createElement('script');
    script.id = SCRIPT_ID;
    script.src = YOUTUBE_IFRAME_API_URL;
    script.async = true;

    script.onerror = () => {
      // Mark the script element so remounts can detect and remove the failed tag.
      script.dataset.error = 'true';
      setError(true);
    };

    // YouTube's IFrame API calls this global when it finishes loading.
    const previousCallback = window.onYouTubeIframeAPIReady;
    window.onYouTubeIframeAPIReady = () => {
      previousCallback?.();
      setReady(true);
    };

    document.body.appendChild(script);
  }, [ready]);

  return { ready, error };
};

// Declare global YouTube IFrame API types so TypeScript is happy.
declare global {
  interface Window {
    YT: {
      Player: new (
        elementId: string | HTMLElement,
        options: {
          videoId: string;
          playerVars?: {
            autoplay?: 0 | 1;
            controls?: 0 | 1;
            rel?: 0 | 1;
            modestbranding?: 0 | 1;
            playsinline?: 0 | 1;
          };
          events?: {
            onReady?: (event: { target: YTPlayer }) => void;
            onStateChange?: (event: { data: number; target: YTPlayer }) => void;
            onError?: (event: { data: number }) => void;
          };
        }
      ) => YTPlayer;
      PlayerState: {
        UNSTARTED: -1;
        ENDED: 0;
        PLAYING: 1;
        PAUSED: 2;
        BUFFERING: 3;
        CUED: 5;
      };
    };
    onYouTubeIframeAPIReady?: () => void;
  }

  interface YTPlayer {
    playVideo(): void;
    pauseVideo(): void;
    stopVideo(): void;
    seekTo(seconds: number, allowSeekAhead: boolean): void;
    setPlaybackRate(rate: number): void;
    getPlaybackRate(): number;
    getCurrentTime(): number;
    getDuration(): number;
    getPlayerState(): number;
    destroy(): void;
  }
}
